import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, OperatorFunction, throwError } from 'rxjs';
import {
    catchError,
    concatMap,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';
import { ConfigurationResultService } from '../../services/configuration-result.service';
import { Product, ProductGroup, ProductsService } from '../../services/products.service';
import { Quote, QuoteService } from '../../services/quote.service';
import { ConfigitConfigurationAndProducts } from '../questionnaire/configit-quest-adapter/configit-types.interface';
import { isEqual } from 'lodash';
import { EnvironmentDiffService } from '../../services/environment-diff.service';

const HEAT_GENERATOR = '366';

const HEAT_PUMP = '367';

export function flatProductList(): OperatorFunction<ProductList, Product[]> {
    return map((list: ProductList) => list.groups.flatMap(({ products }) => products.filter((p) => p.selected)));
}

export interface ProductList {
    groups: ProductGroup[];
    sampleOfferId?: string;
}

export enum ProductEditType {
    quantity = 'quantity',
    optional = 'optional',
    delete = 'delete',
    select = 'select',
}

export interface ProductEditEvent {
    type: ProductEditType;
    productId: string;
    newValue?: number | boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ProductListService {
    private materialToCategoryMap = {
        HeatingBoilers: HEAT_GENERATOR,
        HybridNewBoiler: HEAT_GENERATOR,
        HeatPumps: HEAT_PUMP,
        PropaneHeatPumps: HEAT_PUMP,
        BrineHeatPumps: HEAT_PUMP,
        HybridE3HP: HEAT_PUMP,
        HMUHPCascades: HEAT_PUMP,
        ElectricBoiler: '080',
        BiomassBoiler: '089',
    };
    public isProductListLoading$ = new BehaviorSubject<boolean>(true);
    public productList$: Observable<ProductList>;
    public productListEdits$ = new BehaviorSubject<ProductEditEvent[]>([]);

    constructor(
        private quoteService: QuoteService,
        private configurationResultService: ConfigurationResultService,
        private productService: ProductsService,
        private environmentDiffService: EnvironmentDiffService
    ) {
        this.productList$ = combineLatest([
            this.configurationResultService.configuration$.pipe(
                distinctUntilChanged(
                    (prev, current) =>
                        isEqual(prev?.bomItems, current?.bomItems) &&
                        isEqual(prev?.configuration?.environmentDiff, current?.configuration?.environmentDiff)
                )
            ),
            this.productListEdits$,
        ]).pipe(
            tap(() => this.isProductListLoading$.next(true)),
            switchMap(([configuration, editEvents]) => {
                const quote = this.quoteService.getCurrentQuote();
                if (!configuration || !quote?.quote) {
                    return of(undefined);
                }
                return forkJoin([
                    this.getProductsFromConfiguration(configuration, true, quote),
                    this.getExternalProducts(quote),
                    of(editEvents),
                ]);
            }),
            catchError((error) => {
                this.isProductListLoading$.next(false);
                return throwError(() => error);
            }),
            filter(Boolean),
            map(([{ groups, sampleOfferId }, externalProducts, editEvents]) => ({
                sampleOfferId,
                groups: [...groups, ...externalProducts].map((g) => this.applyEditEvents(g, editEvents)),
            })),
            tap(() => this.isProductListLoading$.next(false)),
            shareReplay(1)
        );
    }

    public validateMainComponent(
        material: string
    ): Observable<{ hasMainComponent: boolean; mainCategory: string; accessoryProducts: string[] }> {
        return this.configurationResultService.configuration$.pipe(
            take(1),
            concatMap((configuration) => this.getProductsFromConfiguration(configuration)),
            flatProductList(),
            map((products) => {
                const mainCategory = this.materialToCategoryMap[material];
                const accessoryProducts = products.filter((p) => p.category !== mainCategory).map((p) => p.material);
                return {
                    hasMainComponent: !mainCategory || products.some((p) => p.category === mainCategory),
                    mainCategory,
                    accessoryProducts,
                };
            })
        );
    }

    public clearEdits() {
        this.productListEdits$.next([]);
    }

    private applyEditEvents(group: ProductGroup, events: ProductEditEvent[]): ProductGroup {
        const deletedProducts = events.filter((e) => e.type === ProductEditType.delete).map((e) => e.productId);
        const products: Product[] = group.products
            .map((product) => {
                return events
                    .filter((e) => e.productId === product.material)
                    .reduce((p, event) => {
                        switch (event.type) {
                            case ProductEditType.optional: {
                                return { ...p, optional: <boolean>event.newValue };
                            }
                            case ProductEditType.quantity: {
                                return { ...p, quantity: <number>event.newValue };
                            }
                            case ProductEditType.select: {
                                return { ...p, selected: <boolean>event.newValue };
                            }
                        }
                        return p;
                    }, product);
            })
            .filter(({ material }) => !deletedProducts.includes(material));
        return { ...group, products };
    }

    private getExternalProducts(quote: Quote): Observable<ProductGroup[]> {
        const lines = this.quoteService.getExternalProductLines(quote.quote);
        const materials = lines.map((line) => ({ material: line.variantCode, quantity: line.quantity }));
        return materials.length
            ? this.productService.getProducts(materials, quote).pipe(
                  map((products) =>
                      products.map((product) => {
                          const line = lines.find((l) => l.variantCode === product.material);
                          return {
                              ...product,
                              translation: line.description.serializedTranslations.reduce(
                                  (obj, ts) => ({ ...obj, [ts.language]: ts.translation }),
                                  {}
                              ),
                              category: 'EXTERNAL',
                          };
                      })
                  ),
                  map((products) =>
                      products.length
                          ? [
                                {
                                    name: 'externalGroup',
                                    translationKey: 'COMMON.CATEGORIES.EXTERNAL_PRODUCT_GROUP',
                                    products,
                                },
                            ]
                          : []
                  )
              )
            : of([]);
    }

    private getProductsFromConfiguration(
        configuration: ConfigitConfigurationAndProducts,
        checkOptional = false,
        quote = this.quoteService.getCurrentQuote()
    ): Observable<ProductList> {
        const sampleOfferId = this.environmentDiffService.getCurrentSampleOfferId(
            configuration.configuration.environmentDiff
        );
        this.quoteService.updateQuoteProperty('sampleQuoteId', sampleOfferId);
        if (sampleOfferId) {
            return this.productService
                .getProductsFromSampleOffer(sampleOfferId, quote)
                .pipe(map((groups) => ({ groups, sampleOfferId })));
        }
        const products = configuration.bomItems.filter((item) => item.componentQuantity > 0);
        if (!quote?.quote || products.length === 0) {
            return of({ groups: [] });
        }
        this.quoteService.pruneSubLines(
            products.map((p) => p.material),
            quote.quote
        );
        return this.productService
            .getProducts(
                products.map((p) => ({
                    material: p.material,
                    quantity: p.componentQuantity,
                    bomItemId: p.itemId,
                })),
                quote,
                checkOptional
            )
            .pipe(
                map((productList) => {
                    const comparator = <T extends { order: number }>(a: T, b: T): number => a.order - b.order;
                    const productGroups: ProductGroup[] = Object.entries(
                        this.productService.productCategoriesGroupNamesMap
                    )
                        .map(([groupName, categoryMap]) => {
                            const categories = categoryMap.categories.flatMap((item) =>
                                Object.entries(item).map(([category, order]) => ({ category, order }))
                            );
                            const products: Product[] = productList
                                .filter((product) => categories.some(({ category }) => category === product.category))
                                .map((product) => ({
                                    ...product,
                                    order: categories.find(({ category }) => category === product.category).order,
                                }))
                                .sort(comparator);
                            return {
                                name: groupName,
                                translationKey: categoryMap['phraseapp-key'],
                                order: categoryMap.order,
                                products,
                            };
                        })
                        .sort(comparator);
                    const otherProducts = productList.filter(
                        (product) =>
                            !productGroups.some(({ products }) => products.some((p) => p.material === product.material))
                    );
                    const otherProductGroup: ProductGroup = this.productService.createOtherProductGroup(otherProducts);
                    const groups = [...productGroups, otherProductGroup].filter((group) => group.products.length);
                    return { groups };
                })
            );
    }
}
